/**
 * `entities/project-tag` API surface.
 *
 * Exposes:
 *   - Service functions for READING tag searches AND for creating tags
 *     (raw service fns; entities may expose raw service fns that features
 *     compose into `useMutation({ mutationFn })` hooks).
 *   - `*Query` queryOptions helpers, so reads flow through queryOptions and
 *     honour the cache-key contract.
 *
 * Workspace-scoped tag pool — shared by project tags and folder tags so the
 * Same tag value dedupes across both surfaces.
 */
import { TagsService, type TagResponseDto } from '@inference.engine/workspace-api-client/web';
import { queryOptions } from '@tanstack/react-query';
import { workspaceHttp } from '@/lib/api/http-client';
import type { ProjectTagsSearchResponse } from '../model/types';
import { PROJECT_TAG_QUERY_KEYS } from './query-keys';

// eslint-disable-next-line i18next/no-literal-string -- tag scope discriminator sent to and stored by the API
export const WORKSPACE_TAG_NAMESPACE = 'workspace';

// ──────────────────────────── Service functions ────────────────────────────

export const searchTags = async (
  query: string,
  namespace?: string,
): Promise<ProjectTagsSearchResponse> => {
  const response = await TagsService.search({
    client: workspaceHttp,
    query: { query, namespace },
  });

  if (!response.data) {
    throw new Error('Failed to search tags');
  }

  return response.data;
};

export const createTag = async (name: string, namespace: string): Promise<TagResponseDto> => {
  const response = await TagsService.create({
    client: workspaceHttp,
    body: { name, namespace },
  });

  if (!response.data) {
    throw new Error('Failed to create tag');
  }

  return response.data;
};

// ──────────────────────────── queryOptions helpers ─────────────────────────

export const workspaceTagsQuery = (workspaceId: string | null | undefined) =>
  queryOptions({
    queryKey: PROJECT_TAG_QUERY_KEYS.workspace(workspaceId!),
    queryFn: () => searchTags('', WORKSPACE_TAG_NAMESPACE),
    enabled: !!workspaceId,
  });
